import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { ZeroState,
  registerDatePickerLocale,
  SharedMessages as sharedMsgs,
} from 'ecs-react-components/dist/index.es';
import { findCcdaExternalSystems } from '../../redux/modules/health-summary/external-systems-mapper';

import EmailForm from './EmailForm';
import CcdaOptionsForm from './CcdaOptions/CcdaOptionsForm';
import SendSecurelyForm from './SendSecurelyForm';
import FileDownloadButton from './FileDownloadButton';
import CcdaList from './CcdaList';
import LoadingIndicatorContainer from '../LoadingIndicatorContainer';
import { callWithLoadingIndicator } from '../../redux/modules/loading-indicator/loading-indicator-middleware';
import { messages } from './HealthSummary.i18n';

import {
  loadHealthSummaryHtml,
  getMostRecentHealthSummaryIdentifier,
  getMostRecentOfficeVisitsForPatient,
} from '../../redux/modules/health-summary/health-summary-middleware';

import { clearLoadedHtml } from '../../redux/modules/health-summary/actions/creators';

function HealthInformationSummary() {
  const dispatch = useDispatch();

  const { patientId } = useSelector((state) => state.profile.patient);
  const {
    currentIdentifier,
    filteredCcdas,
    healthSummaryHtml,
  } = useSelector((state) => state.healthSummary);

  const language = useSelector((state) => state.language.languageCode);

  const externalSystems = useSelector((state) => state.externalSystems.externalSystems);
  const [ccdaExternalSystems, setCcdaExternalSystems] = useState(null);

  useEffect(() => {
    if (externalSystems && externalSystems.length) {
      const extSystems = findCcdaExternalSystems(externalSystems);
      setCcdaExternalSystems(extSystems);
    }

    registerDatePickerLocale(language);
    dispatch(getMostRecentOfficeVisitsForPatient(patientId, language));
  }, []);

  useEffect(() => {
    if (
      patientId &&
      ccdaExternalSystems &&
      ccdaExternalSystems.length === 1 &&
      !currentIdentifier
    ) {
      // Find the most recent ccda identifier only when no html is saved in the redux store
      dispatch(getMostRecentHealthSummaryIdentifier(patientId, ccdaExternalSystems[0].ifSystemId));
    }
    return () => {
      dispatch(clearLoadedHtml());
    }
  }, [ccdaExternalSystems, patientId])

  useEffect(() => {
    // Download the ccda html only when no html is saved in the redux store
    if (!healthSummaryHtml) {
      let extSystem = externalSystems[0];
      if (currentIdentifier) {
        extSystem = externalSystems.find((x) => x.ifSystemId === currentIdentifier.ifSystemId);
      }
      if (extSystem && currentIdentifier) {
        dispatch(
          callWithLoadingIndicator(
            'HealthSummary',
            () => dispatch(loadHealthSummaryHtml(patientId, currentIdentifier, extSystem.patientDisplayName))
          )
        );
      }
    }
  }, [currentIdentifier]);

  const renderCcdaList = () => (<CcdaList filteredCcdas={filteredCcdas} />);

  const renderZeroState = (messageId) => (
    <ZeroState zeroStateMessage={ <FormattedMessage { ...messageId } /> }
      stateIconCss="fa-regular fa-file-lines"
    />
  );

  const renderCcda = () => (
    <div data-qa-container="healthSummaryContainer">
        <p>
          <FormattedMessage {...messages.page.introduction} />
        </p>
      <div className="ccda-container" tabIndex="0" dangerouslySetInnerHTML={{ __html: healthSummaryHtml }}></div>
    </div>
  );

  const renderContent = () => {
    if (filteredCcdas.length > 1) {
      return renderCcdaList();
    }
    // Based on the number of external systems and the presence of HTML to view, show the user some content
    if (healthSummaryHtml) {
      return renderCcda();
    }

    if (ccdaExternalSystems && ccdaExternalSystems.length > 1) {
      if (externalSystems.userSelectedASystem) {
        // No html loaded and have selected external system Id
        return renderZeroState(messages.page.noHealthSummary);
      }
      // No html loaded and have more than one external systems and
      // user has not selected any options.
      return renderZeroState(messages.page.multipleExternalSystems);
    }

    return renderZeroState(messages.page.noHealthSummary);
  }

  return (
    <div id="health-summary-content">
      <div className="row justify-content-between mb-3">
        <div className="col-auto">
          <h1 id="content" className="float-left mr-2" data-qa-item="pageHeader" tabIndex="0">
            <FormattedMessage { ...messages.page.title } />
          </h1>
        </div>
        <div className="col-auto">
          <div className="float-md-end mt-4">
          {
            healthSummaryHtml ?
            <span>
              <EmailForm />
              <SendSecurelyForm />
              <FileDownloadButton />
            </span>
              : null
          }
          {
            ccdaExternalSystems && ccdaExternalSystems.length > 0
              ? <CcdaOptionsForm />
              : null
          }
          <span className="sr-only" id="opensPopupLabel">
            <FormattedMessage {...sharedMsgs.labels.openNewDialog} />
          </span>
          <span className="sr-only" id="downloadMessage">
            <FormattedMessage {...messages.page.downloadMessage} />
          </span>
          </div>
        </div>
      </div>
      <LoadingIndicatorContainer loadingKey="HealthSummary" >
        {
          renderContent()
        }
      </LoadingIndicatorContainer>
    </div>
  );
}

export default HealthInformationSummary;